import { useEffect, useState } from 'react';
import { ErrorNotice } from '../components/ErrorNotice';
import {
  Badge,
  Button,
  Card,
  CodeBlock,
  ConfirmDialog,
  EmptyState,
  Field,
  Loading,
  Modal,
  Notice,
} from '../components/ui';
import { API_URL } from '../config/env';
import { createMcpCredential, listMcpCredentials, revokeMcpCredential } from '../data/mcp';
import type { CreateMcpCredentialResponse, McpCredentialSummary } from '@occ/contracts';
import { timeAgo } from '../lib/format';

/**
 * Mi cuenta — credenciales personales MCP (RF-13).
 *
 * Es autoservicio a propósito: cada usuario conecta su propio MCP cuando
 * quiera, sin pedirle un token a nadie. El comando de un clic viene armado
 * aquí mismo con `API_URL` porque es lo único que el panel sabe y el usuario
 * no tiene por qué saber — es la misma puerta que ya usa para todo lo demás.
 *
 * Sin expiración de tiempo (a diferencia del JWT de 15 min del panel): la
 * credencial vive hasta que se revoca a mano, aquí o desde otra máquina.
 */

const mcpAddCommand = (token: string): string =>
  `claude mcp add --transport http occ ${API_URL}/mcp --header "Authorization: Bearer ${token}"`;

export function AccountPage() {
  const [credentials, setCredentials] = useState<McpCredentialSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<CreateMcpCredentialResponse | null>(null);
  const [revoking, setRevoking] = useState<McpCredentialSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    setError(null);
    listMcpCredentials()
      .then(setCredentials)
      .catch((cause: unknown) => setError(cause));
  };

  useEffect(refresh, []);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const credential = await createMcpCredential(name.trim() || 'MCP');
      setIssued(credential);
      setName('');
      refresh();
    } catch (cause) {
      setError(cause);
    } finally {
      setCreating(false);
    }
  };

  const submitRevoke = async () => {
    if (!revoking) return;
    setBusy(true);
    try {
      await revokeMcpCredential(revoking.id);
      setRevoking(null);
      refresh();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="grid gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Mi cuenta</h1>
        <p className="text-sm opacity-60">
          Credenciales personales para conectar tu propio MCP. No caducan solas: viven hasta que las
          revocas.
        </p>
      </div>

      <Card title="Nueva credencial MCP">
        <form
          className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <Field
            label="Nombre"
            value={name}
            placeholder="Laptop trabajo"
            hint="Una etiqueta libre para reconocerla después en la lista."
            onChange={(event) => setName(event.target.value)}
          />
          <div>
            <Button type="submit" variant="primary" disabled={creating}>
              {creating ? 'Generando…' : 'Generar credencial'}
            </Button>
          </div>
        </form>

        {error !== null && <ErrorNotice error={error} what="Generar o listar credenciales MCP" />}
      </Card>

      <Card title="Credenciales MCP">
        {!credentials ? (
          <Loading label="Consultando credenciales…" />
        ) : credentials.length === 0 ? (
          <EmptyState title="Todavía no tienes ninguna">
            Genera una arriba y pega el comando en tu máquina.
          </EmptyState>
        ) : (
          <ul className="grid gap-2">
            {credentials.map((credential) => (
              <li
                key={credential.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-black/10 dark:border-white/10 px-3 py-2 text-sm"
              >
                <div className="grid gap-0.5">
                  <span className="font-medium">{credential.name}</span>
                  <span className="opacity-60">
                    creada {timeAgo(credential.createdAt)}
                    {credential.lastUsedAt && <> · último uso {timeAgo(credential.lastUsedAt)}</>}
                  </span>
                </div>
                {credential.revokedAt ? (
                  <Badge tone="warn">revocada</Badge>
                ) : (
                  <Button variant="ghost" onClick={() => setRevoking(credential)}>
                    Revocar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <IssuedCredentialModal token={issued} onClose={() => setIssued(null)} />

      <ConfirmDialog
        open={revoking !== null}
        title="Revocar credencial MCP"
        confirmLabel="Revocar"
        busy={busy}
        onCancel={() => setRevoking(null)}
        onConfirm={() => void submitRevoke()}
      >
        <p>
          <strong>{revoking?.name}</strong> dejará de servir en la siguiente llamada. Cualquier MCP
          conectado con ella empezará a recibir 401.
        </p>
      </ConfirmDialog>
    </section>
  );
}

/**
 * El token, una sola vez — igual que el de enrollment: el OCC solo guarda su
 * hash, así que perderlo de vista aquí es perderlo para siempre.
 */
function IssuedCredentialModal({
  token,
  onClose,
}: {
  token: CreateMcpCredentialResponse | null;
  onClose: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (token) setAcknowledged(false);
  }, [token]);

  if (!token) return null;

  return (
    <Modal
      open
      wide
      title="Credencial generada — se muestra una sola vez"
      onClose={() => (acknowledged ? onClose() : setAcknowledged(true))}
      footer={
        <Button
          variant={acknowledged ? 'primary' : 'secondary'}
          onClick={() => (acknowledged ? onClose() : setAcknowledged(true))}
        >
          {acknowledged ? 'Cerrar' : 'Ya lo copié'}
        </Button>
      }
    >
      <div className="grid gap-4">
        <Notice tone="warn" title="El OCC solo guarda el hash de esta credencial">
          En cuanto cierres este diálogo no hay forma de volver a verla. Si la pierdes, genera otra
          — la anterior sigue válida hasta que la revoques.
        </Notice>

        <CodeBlock label="Comando (pégalo en tu terminal)" value={mcpAddCommand(token.token)} />

        <p className="text-xs opacity-60">
          Registra el MCP en tu Claude Code local con esta credencial. No expira sola: revócala
          desde aquí cuando ya no la uses.
        </p>
      </div>
    </Modal>
  );
}
